/**
 * The before/after lens of a v2 run (BENCH-V2-SPEC §13.1 to §13.3), built from the run report alone, so a finished job reads
 * back from its record with no re-analysis: the document-scale score of each channel before and after (Tier C), each unit
 * with its status, its diff against the original and its dialogue-guard notes, the ledger's reverted units, the D-route goals
 * and the coverage counts of the evidence items by state (§4.5).
 */
import type { ChannelId } from '../channels/types.ts';
import { type ParagraphDiff, diffParagraphs } from './diff.ts';
import { type FailureReport, SEVERITIES, type Severity } from './failure-report.ts';
import type { InventoryItem, ItemState, UnitLocal } from './inventory.ts';
import { type UnitBar, unitBar } from './keep.ts';
import type { AttemptSummary, ChunkStatus, GoalReport, RunReport, UnitStatus } from './loop2.ts';
import type { StrategyId } from './prompts2.ts';
import type { VersionNote } from './versions.ts';

export type UnitMark =
	| 'accepted'
	| 'partial'
	| 'failed'
	| 'kept-best'
	| 'kept-lenient'
	| 'reverted'
	| 'not-selected'
	| 'not-actionable'
	| 'document-route'
	| 'declined';

export interface ChannelScore {
	id: ChannelId;
	before: number | null;
	/** After the run's strict result (Tier C): without the lenient keeps, when the run had any. */
	after: number | null;
	delta: number | null;
	/** Above its baseline plus its floor after the run: shown first. */
	over: boolean;
	/** With the lenient keeps (ED-94); present only for a run whose lenient pass kept a unit. */
	withLenient?: number | null;
	deltaLenient?: number | null;
	overLenient?: boolean;
	/** The lenient keeps that raised this channel's document score, the largest first (never merged with other channels). */
	contributors?: Array<{ unit: string; points: number }>;
	/**
	 * Above its baseline plus its floor although no rewrite targets it (route N, ED-130): shown apart from the channels
	 * over, with the reason, never as a fault of the rewrite.
	 */
	notRewritten?: string;
}

/** The signal score record of the attempt the unit kept (or, for a unit left alone, its last attempt): §5.8. */
export interface UnitSignal {
	/** `S`: judged by the signal score; `events`: the unit carried less than `B_MIN` points (ED-57). */
	rule: 'S' | 'events' | 'legacy';
	S: number;
	R: number;
	I: number;
	B: number;
	/** Δp of every channel that moved, in points, the largest move first (channels are never merged). */
	channels: Array<{ channel: ChannelId; points: number }>;
}

/** Which version of a request of several the unit's kept (or last) attempt was, and what became of the others (ED-117). */
export interface LensVersion {
	strategy: StrategyId;
	others: VersionNote[];
}

export interface UnitLens {
	id: string;
	para: number;
	paraEnd: number;
	route: 'S' | 'P' | 'D';
	channels: ChannelId[];
	mark: UnitMark;
	/** A unit the run accepted at a lower standard than the rest (§6.8): kept best (strict) or kept (lenient). */
	keptBest: boolean;
	/** Kept by the lenient tier (ED-92). */
	keptLenient: boolean;
	/** The failure report of a kept or failed unit (ED-93). */
	report?: FailureReport;
	messages: string[];
	/** The dialogue guard's sentences from the unit's attempts (§6.5). */
	guard: string[];
	/** Under the guardrails (GR3, ED-196): the prose review's sentences from the unit's attempts; absent when none. */
	review?: string[];
	attempts: number;
	signal?: UnitSignal;
	/** Only for a unit whose requests asked for several versions; shown in the unit's detail, not in the report's sentence. */
	version?: LensVersion;
	/**
	 * Under the guardrails (GR2, ED-194): the reason the editor left the passage as it is ("Left as it is: reason"). Present
	 * only for a `declined` unit.
	 */
	declined?: string;
	/** Under the guardrails: the rationale of the rewrite the unit kept (accepted or kept best), shown under its diff. */
	why?: string;
	/**
	 * With the review's meaning-only rule (GR3B): the review's note on that rationale (the reason names no specific benefit,
	 * or the change does not deliver it), shown under it; never a failure. Absent otherwise.
	 */
	reviewNote?: string;
	/**
	 * The localised figure of each targeted channel (UD-45): the channel's change-point segment that holds the unit in the
	 * run's baseline analysis. Shown in the lens since 2.32.0 (UD-46). Absent for a run from before 2.31.0.
	 */
	local?: UnitLocal[];
	/** Empty when the text is unchanged. */
	diff: ParagraphDiff[];
	original: string;
	text: string;
}

export interface ChunkLens2 {
	key: string;
	a: number;
	b: number;
	status: ChunkStatus;
	label: string;
	messages: string[];
	rounds: number;
	units: string[];
}

export interface Lens2 {
	/** The document the lens belongs to; the lens is shown only for it (§15 item 8). */
	docHash: string;
	channels: ChannelScore[];
	units: UnitLens[];
	chunks: ChunkLens2[];
	/** Units the ledger reverted (§5.7), with the reason. */
	reverted: Array<{ id: string; reason: string }>;
	/** Units the guardrails' editor left as they are (GR2), with the reason, in text order; empty without the guardrails. */
	declined: Array<{ id: string; reason: string }>;
	/** Kept units (strict and lenient) by the severity of their report, worst first (ED-93); zero counts are left out. */
	severities: Array<{ severity: Severity; kept: number; lenient: number; failed: number }>;
	/** ED-66's accounting, lenient keeps counted as failed and reported separately. */
	bar: UnitBar;
	/** The lenient pass kept at least one unit: the channel table carries both analyses. */
	lenient: boolean;
	goals: GoalReport[];
	coverage: {
		chunks: number;
		records: number;
		items: number;
		byChannel: Array<{ channel: ChannelId; counts: Partial<Record<ItemState, number>> }>;
	};
	status: RunReport['status'];
}

const CHUNK_LABEL: Record<ChunkStatus, string> = {
	accepted: 'accepted',
	partial: 'partially addressed',
	failed: 'failed',
	untouched: 'nothing to change',
};

const MARK: Record<UnitStatus, UnitMark> = {
	pending: 'not-selected',
	addressed: 'accepted',
	partial: 'partial',
	failed: 'failed',
	'kept-best': 'kept-best',
	reverted: 'reverted',
	'not-selected': 'not-selected',
	declined: 'declined',
};

/** The rationale of the rewrite a unit kept (GR2): the accepted or kept-best attempt's `why`. */
export function whyOf(attempts: readonly AttemptSummary[]): string | undefined {
	return attempts.find((x) => x.outcome === 'accepted' || x.outcome === 'kept-best')?.why;
}

/** The review's note on the rationale of the rewrite a unit kept (GR3B): the accepted or kept-best attempt's `reviewNote`. */
export function reviewNoteOf(attempts: readonly AttemptSummary[]): string | undefined {
	return attempts.find((x) => x.outcome === 'accepted' || x.outcome === 'kept-best')?.reviewNote;
}

/** The attempt whose signal record the lens shows: the accepted or kept one, else the last one that was measured. */
export function signalOf(attempts: readonly AttemptSummary[]): UnitSignal | undefined {
	const measured = attempts.filter((a) => a.S !== undefined);
	const a =
		measured.find((x) => x.outcome === 'accepted' || x.outcome === 'kept-best') ??
		measured[measured.length - 1];
	if (!a || a.S === undefined) return undefined;
	return {
		rule: a.rule ?? 'legacy',
		S: a.S,
		R: a.R ?? 0,
		I: a.I ?? 0,
		B: a.B ?? 0,
		channels: Object.entries(a.delta ?? {})
			.map(([channel, points]) => ({ channel: channel as ChannelId, points }))
			.sort((x, y) => Math.abs(y.points) - Math.abs(x.points)),
	};
}

/** The version of the attempt the lens shows (the kept or accepted one, else the last that names one). */
export function versionOf(attempts: readonly AttemptSummary[]): LensVersion | undefined {
	const named = attempts.filter((a) => a.strategy !== undefined);
	const a =
		named.find((x) => x.outcome === 'accepted' || x.outcome === 'kept-best') ??
		named[named.length - 1];
	return a?.strategy ? { strategy: a.strategy, others: a.others ?? [] } : undefined;
}

export function markOf(
	status: UnitStatus,
	keptBest: boolean | undefined,
	keptLenient?: boolean | undefined
): UnitMark {
	return keptLenient ? 'kept-lenient' : keptBest ? 'kept-best' : MARK[status];
}

export function itemCounts(
	items: readonly InventoryItem[]
): Array<{ channel: ChannelId; counts: Partial<Record<ItemState, number>> }> {
	const by = new Map<ChannelId, Partial<Record<ItemState, number>>>();
	for (const it of items) {
		const c = by.get(it.channel) ?? {};
		c[it.state] = (c[it.state] ?? 0) + 1;
		by.set(it.channel, c);
	}
	return [...by.entries()]
		.sort((a, b) => a[0].localeCompare(b[0]))
		.map(([channel, counts]) => ({ channel, counts }));
}

export function buildLens2(report: RunReport, docHash: string): Lens2 {
	const tc = report.tierC;
	const ids = new Set<ChannelId>();
	if (tc) {
		for (const c of Object.keys(tc.baseline) as ChannelId[]) ids.add(c);
		for (const c of Object.keys(tc.final) as ChannelId[]) ids.add(c);
	}
	const len = tc?.lenient;
	if (len) for (const c of Object.keys(len.final) as ChannelId[]) ids.add(c);
	const over = new Set(tc?.over ?? []);
	const overLenient = new Set(len?.over ?? []);
	const notRewritten = new Map(
		[...(tc?.notRewritten ?? []), ...(len?.notRewritten ?? [])].map((x) => [x.channel, x.reason])
	);
	const channels: ChannelScore[] = [...ids]
		.map((id) => {
			const before = tc?.baseline[id] ?? null;
			const after = tc?.final[id] ?? null;
			const withLenient = len ? (len.final[id] ?? null) : undefined;
			return {
				id,
				before,
				after,
				delta: before !== null && after !== null ? after - before : null,
				over: over.has(id),
				...(notRewritten.has(id) ? { notRewritten: notRewritten.get(id) as string } : {}),
				...(len
					? {
							withLenient,
							deltaLenient:
								before !== null && withLenient !== null && withLenient !== undefined
									? withLenient - before
									: null,
							overLenient: overLenient.has(id),
							...(len.contributors[id] ? { contributors: len.contributors[id] } : {}),
						}
					: {}),
			};
		})
		.sort(
			(a, b) =>
				Number(b.over || b.overLenient === true) - Number(a.over || a.overLenient === true) ||
				a.id.localeCompare(b.id)
		);

	const units: UnitLens[] = report.units.map((u) => {
		const st = report.unitStates[u.id];
		const original = report.originals[u.id] ?? '';
		const text = st?.text ?? original;
		const guard = [...new Set((st?.attempts ?? []).flatMap((a) => a.guard))];
		const review = [...new Set((st?.attempts ?? []).flatMap((a) => a.review ?? []))];
		const why = st ? whyOf(st.attempts) : undefined;
		const reviewNote = st ? reviewNoteOf(st.attempts) : undefined;
		return {
			id: u.id,
			para: u.para,
			paraEnd: u.paraEnd,
			route: u.route,
			channels: u.channels,
			mark: st ? markOf(st.status, st.keptBest, st.keptLenient) : 'not-selected',
			keptBest: st?.keptBest === true,
			keptLenient: st?.keptLenient === true,
			...(st?.report ? { report: st.report } : {}),
			messages: st?.messages ?? [],
			guard,
			...(review.length ? { review } : {}),
			attempts: st?.attempts.filter((a) => a.outcome !== 'held').length ?? 0,
			...(st && signalOf(st.attempts) ? { signal: signalOf(st.attempts) as UnitSignal } : {}),
			...(st && versionOf(st.attempts) ? { version: versionOf(st.attempts) as LensVersion } : {}),
			...(st?.status === 'declined' ? { declined: st.reason ?? '' } : {}),
			...(why && st?.status !== 'declined' ? { why } : {}),
			...(reviewNote && st?.status !== 'declined' ? { reviewNote } : {}),
			...(u.local ? { local: u.local } : {}),
			diff: text === original ? [] : diffParagraphs(original, text),
			original,
			text,
		};
	});

	return {
		docHash,
		channels,
		units,
		chunks: report.chunks.map((c) => ({
			key: c.key,
			a: c.a,
			b: c.b,
			status: c.status,
			label: CHUNK_LABEL[c.status],
			messages: c.messages,
			rounds: c.rounds,
			units: c.units,
		})),
		reverted: units
			.filter((u) => u.mark === 'reverted')
			.map((u) => ({ id: u.id, reason: u.messages[0] ?? 'reverted' })),
		declined: units
			.filter((u) => u.declined !== undefined)
			.sort((a, b) => a.para - b.para)
			.map((u) => ({ id: u.id, reason: u.declined as string })),
		severities: SEVERITIES.map((severity) => ({
			severity,
			kept: units.filter((u) => u.keptBest && u.report?.severity === severity).length,
			lenient: units.filter((u) => u.keptLenient && u.report?.severity === severity).length,
			failed: units.filter((u) => u.mark === 'failed' && u.report?.severity === severity).length,
		})).filter((x) => x.kept + x.failed > 0),
		bar: unitBar(report.units.map((u) => report.unitStates[u.id])),
		lenient: len !== undefined,
		goals: report.goals,
		coverage: {
			chunks: report.coverage.chunks,
			records: report.coverage.records,
			items: report.coverage.items,
			byChannel: itemCounts(report.items),
		},
		status: report.status,
	};
}

/** The units "Revert all kept failures" would put back (§13.1): the strict and the lenient keeps. */
export const keptBestIds = (report: RunReport): string[] =>
	report.units.filter((u) => report.unitStates[u.id]?.keptBest).map((u) => u.id);

/** The units the lenient pass kept ("Revert all lenient", BENCH-V3-PLAN §7.3). */
export const keptLenientIds = (report: RunReport): string[] =>
	report.units.filter((u) => report.unitStates[u.id]?.keptLenient).map((u) => u.id);

/** The kept units (either tier) whose failure report has this severity ("Revert all style", "Revert all signal-negative"). */
export const keptIdsBySeverity = (report: RunReport, severity: Severity): string[] =>
	report.units
		.filter((u) => {
			const st = report.unitStates[u.id];
			return st?.keptBest === true && st.report?.severity === severity;
		})
		.map((u) => u.id);

/**
 * The text with the units in `ids` put back to their original text (§13.1 "Revert to original", per unit, per chunk or for
 * every kept failure). Pure: it works on the report's text and the units' spans, last unit first so earlier spans stay valid.
 */
export function revertedText(report: RunReport, ids: ReadonlySet<string>): string {
	const picked = report.units
		.filter((u) => ids.has(u.id) && report.spans[u.id] !== undefined)
		.sort(
			(a, b) =>
				(report.spans[b.id] as [number, number])[0] - (report.spans[a.id] as [number, number])[0]
		);
	let text = report.text;
	for (const u of picked) {
		const [s, e] = report.spans[u.id] as [number, number];
		text = text.slice(0, s) + (report.originals[u.id] ?? '') + text.slice(e);
	}
	return text;
}
